import { ChevronRightIcon } from '@heroicons/react/20/solid'
import dayjs from 'dayjs'
import PropTypes from 'prop-types'
import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { twMerge as mergeClassNames } from 'tailwind-merge'

import {
  BUCKET,
  bucketMeta,
  INTERNAL_BUCKETS,
  OUTSTANDING_BUCKETS,
  PERIODS,
  SLA_LATE,
  SUBMITTED_BUCKETS,
  TOP_BUCKETS,
} from './buckets'

// Closed and submitted is the last of the top buckets, but it shows once only: at the head
// of its own breakdown, below the Adjudication line (2026-09-28).
const TOP_ROWS = [BUCKET.RECEIVED, ...TOP_BUCKETS.filter((bucket) => bucket !== BUCKET.SUBMITTED)]

// Rows that show dollars beneath the count, where WARP has them, rather than a share of
// Received: everything under Closed and submitted, and Internal's split.
const MONEY_ROWS = [...SUBMITTED_BUCKETS, ...INTERNAL_BUCKETS]

// Top rows that expand into the rows that make them up: Outstanding by whose review it
// is (2026-10-06), Internal into Internal itself and pre-submission write-offs
// (2026-09-24).
const EXPANSIONS = {
  [BUCKET.OUTSTANDING]: OUTSTANDING_BUCKETS,
  [BUCKET.INTERNAL]: INTERNAL_BUCKETS,
}

/**
 * Only Received is emphasised: it is the total the work rows beneath it add up to.
 * Mark's prototype (FR-11) also emphasised Ready and Outstanding; changed 2026-09-24 so
 * the one bold row reads as the total.
 */
const EMPHASISED = [BUCKET.RECEIVED]

const countFor = (period, bucket) =>
  bucket === BUCKET.RECEIVED ? period.received : period.buckets[bucket] ?? 0

const formatDollars = (value) =>
  `$${Math.round(Number(value) || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`

/**
 * The calendar dates a period covers, e.g. "Thu Sep 24" or "Sep 1 – 24".
 *
 * Read from the date part of the API's ISO strings, which are already in the dealer's
 * time zone. Parsing them as instants would move them into the browser's zone and could
 * shift a boundary by a day.
 */
const formatPeriodDates = (period) => {
  if (!period?.from || !period?.to) return null
  const from = dayjs(period.from.slice(0, 10))
  const to = dayjs(period.to.slice(0, 10))

  if (from.isSame(to, 'day')) return from.format('ddd MMM D')
  // A whole calendar year (Prior year) reads best as just the year.
  if (
    from.format('MM-DD') === '01-01' &&
    to.format('MM-DD') === '12-31' &&
    from.isSame(to, 'year')
  ) {
    return from.format('YYYY')
  }
  if (from.isSame(to, 'month')) return `${from.format('MMM D')} – ${to.format('D')}`
  if (from.isSame(to, 'year')) return `${from.format('MMM D')} – ${to.format('MMM D')}`
  return `${from.format('MMM D, YYYY')} – ${to.format('MMM D, YYYY')}`
}

/**
 * The prototype's status table: the work stage for every RO received, then how the OEM
 * adjudicated the ones that were submitted. Every cell is a real button, because every
 * number is a question someone wants to open.
 */
const StatusMatrix = ({
  beforePrior,
  labelLinks,
  loading,
  oemName,
  onSelect,
  periods,
  selected,
  selfManaged,
  showLate,
}) => {
  const [expanded, setExpanded] = useState({})
  const toggle = (bucket) => setExpanded((open) => ({ ...open, [bucket]: !open[bucket] }))
  const metaFor = (bucket) => bucketMeta(bucket, { selfManaged })

  const renderLabel = (bucket, { indent = false } = {}) => {
    const meta = metaFor(bucket)
    const emphasised = EMPHASISED.includes(bucket)
    const href = labelLinks[bucket]
    const expandable = Boolean(EXPANSIONS[bucket])
    const open = Boolean(expanded[bucket])

    return (
      <th
        className={mergeClassNames('px-4 py-1.5 text-left align-middle', indent && 'pl-9')}
        scope="row"
      >
        <span className="flex items-center gap-2">
          <i
            aria-hidden
            className="h-2.5 w-2.5 shrink-0 rounded-sm"
            style={{ backgroundColor: meta.swatch }}
          />
          {/* Status words link to their lists (2026-09-24): Ready to the ready queue,
              Outstanding to the Outstanding List. */}
          {href ? (
            <Link
              className="text-charcoal-700 decoration-charcoal-300 text-sm font-medium leading-tight underline underline-offset-2 hover:text-blue-800 hover:decoration-blue-800"
              to={href}
            >
              {meta.label}
            </Link>
          ) : (
            <span
              className={mergeClassNames(
                'text-sm leading-tight',
                emphasised ? 'text-charcoal-900 font-semibold' : 'text-charcoal-600 font-medium',
              )}
            >
              {meta.label}
            </span>
          )}
          {expandable && (
            <button
              aria-expanded={open}
              aria-label={`${open ? 'Hide' : 'Show'} the ${meta.label} breakdown`}
              className="text-charcoal-400 hover:text-charcoal-900 rounded"
              onClick={() => toggle(bucket)}
              type="button"
            >
              <ChevronRightIcon
                aria-hidden
                className={mergeClassNames('h-4 w-4 transition-transform', open && 'rotate-90')}
              />
            </button>
          )}
        </span>
        <span className="text-charcoal-400 block pl-[18px] text-[11px] font-normal leading-tight">
          {meta.description(oemName)}
        </span>
      </th>
    )
  }

  const renderCell = (bucket, periodMeta) => {
    const meta = metaFor(bucket)
    const period = periods.find((row) => row.id === periodMeta.id)
    const count = period ? countFor(period, bucket) : null
    const isMoneyRow = MONEY_ROWS.includes(bucket)
    const isSelected = selected?.periodId === periodMeta.id && selected?.bucket === bucket
    const late = showLate && bucket === BUCKET.READY ? period?.late?.ready ?? 0 : 0

    // Top rows show their share of Received. The rows under Closed and submitted, and
    // Internal's split, show dollars instead where WARP has them: claimed for pending,
    // lost for written off.
    let secondary = null
    if (period && count !== null) {
      if (isMoneyRow) {
        if (meta.amount && count) secondary = formatDollars(period.amounts?.[meta.amountKey])
      } else if (bucket !== BUCKET.RECEIVED) {
        secondary = `${period.received ? Math.round((count / period.received) * 100) : 0}%`
      }
    }

    // The accessible name is the whole cell read out: a screen reader user should not
    // have to rebuild it from the row and column headers.
    const label = [
      meta.label,
      periodMeta.label.toLowerCase(),
      `${count ?? 0} RO${count === 1 ? '' : 's'}`,
      secondary && isMoneyRow ? `${secondary} ${meta.amount}` : null,
      late > 0 ? `${late} past SLA` : null,
    ]
      .filter(Boolean)
      .join(', ')

    return (
      <td
        className={mergeClassNames(
          'p-1 align-middle',
          periodMeta.rule && 'border-charcoal-200 border-l-2',
        )}
        key={periodMeta.id}
        style={{ backgroundColor: periodMeta.tint }}
      >
        <button
          aria-label={label}
          aria-pressed={isSelected}
          className={mergeClassNames(
            // One line: the count, then its share or dollars, then the late pill. The table
            // fits on one screen this way (2026-10-12); stacked, it ran past the bottom.
            'focus-visible:ring-blue flex w-full flex-wrap items-baseline justify-center gap-x-1.5 rounded-lg px-2 py-1.5 transition-colors focus-visible:outline-none focus-visible:ring-2 disabled:cursor-default',
            isSelected ? 'ring-2 ring-blue-800' : 'enabled:hover:bg-charcoal-100/60',
          )}
          disabled={!period}
          onClick={() => onSelect({ periodId: periodMeta.id, bucket })}
          type="button"
        >
          <span
            className={mergeClassNames(
              'tabular-nums',
              EMPHASISED.includes(bucket) ? 'text-lg font-semibold' : 'text-base',
              count ? 'text-charcoal-900' : 'text-charcoal-300',
              loading && 'opacity-40',
            )}
          >
            {count === null ? '—' : count.toLocaleString()}
          </span>

          {secondary && (
            <span
              className={mergeClassNames(
                'text-[11px] tabular-nums',
                count ? 'text-charcoal-500' : 'text-charcoal-300',
                loading && 'opacity-40',
              )}
            >
              {secondary}
            </span>
          )}

          {late > 0 && (
            <span
              className="self-center whitespace-nowrap rounded-full px-1.5 py-px text-[10px] font-bold"
              style={{ backgroundColor: SLA_LATE.fill, color: SLA_LATE.ink }}
            >
              {late} late
            </span>
          )}
        </button>
      </td>
    )
  }

  const renderRow = (bucket, { indent = false, className = '' } = {}) => (
    <tr className={mergeClassNames('border-charcoal-100 border-t', className)} key={bucket}>
      {renderLabel(bucket, { indent })}
      {PERIODS.map((periodMeta) => renderCell(bucket, periodMeta))}
    </tr>
  )

  return (
    <div className="overflow-hidden rounded-2xl bg-white shadow-sm">
      <table className="w-full table-fixed border-collapse">
        <thead>
          <tr>
            {/* Wide enough that no status name wraps, so every row stays one line high. */}
            <th className="text-charcoal-700 w-[276px] px-4 py-2 text-left text-[10px] font-bold uppercase tracking-widest">
              Work status
            </th>
            {PERIODS.map((meta) => (
              <th
                className={mergeClassNames(
                  'text-charcoal-700 px-2 py-2 text-center text-[10px] font-bold uppercase tracking-widest',
                  meta.rule && 'border-charcoal-200 border-l-2',
                )}
                key={meta.id}
                style={{ backgroundColor: meta.tint }}
              >
                {meta.label}
                {/* Always rendered, so the header does not change height when the
                      summary arrives. */}
                <span className="text-charcoal-500 mt-0.5 block text-[11px] font-normal normal-case tracking-normal">
                  {formatPeriodDates(periods.find((row) => row.id === meta.id)) || ' '}
                </span>
                {/* The table stops at the prior year; the Outstanding List does not.
                    Flag what is still on it from before then (2026-09-24). */}
                {meta.id === 'prior' && beforePrior?.outstanding > 0 && (
                  <button
                    aria-label={`${beforePrior.outstanding} outstanding repair order${
                      beforePrior.outstanding === 1 ? '' : 's'
                    } received before ${beforePrior.year}`}
                    className="mt-1 inline-flex items-center gap-0.5 whitespace-nowrap rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold normal-case tracking-normal text-amber-900 hover:bg-amber-200"
                    onClick={() => onSelect({ periodId: 'older', bucket: BUCKET.OUTSTANDING_ANY })}
                    title={`Still on the Outstanding List, received before ${beforePrior.year}`}
                    type="button"
                  >
                    * {beforePrior.outstanding.toLocaleString()} before {beforePrior.year}
                  </button>
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {TOP_ROWS.flatMap((bucket) => [
            renderRow(bucket),
            ...(expanded[bucket]
              ? (EXPANSIONS[bucket] || []).map((child) => renderRow(child, { indent: true }))
              : []),
          ])}

          {/* The Adjudication line, then Closed and submitted (the last of the rows that
              add up to Received) and the five rows that always add up to it (Mark,
              2026-09-24). WARP records no paid amount, so Paid rows show counts only. */}
          <tr className="border-charcoal-200 bg-charcoal-50 border-t-2">
            <td className="text-charcoal-500 px-4 py-1.5 text-xs" colSpan={PERIODS.length + 1}>
              <b className="text-charcoal-900 font-semibold">
                Adjudication by {oemName || 'the OEM'}
              </b>{' '}
              — of closed and submitted; counts, with dollars claimed for pending and lost for
              written off
            </td>
          </tr>
          {/* White like every other row's label: only the Adjudication line is shaded. */}
          {renderRow(BUCKET.SUBMITTED, { className: 'border-t-0' })}
          {SUBMITTED_BUCKETS.map((bucket) => renderRow(bucket, { indent: true }))}
        </tbody>
      </table>
    </div>
  )
}

StatusMatrix.propTypes = {
  // Outstanding List ROs received before the prior year: `{ outstanding, year, to }`.
  beforePrior: PropTypes.shape({
    outstanding: PropTypes.number,
    to: PropTypes.string,
    year: PropTypes.number,
  }),
  // Rows whose label links to a list, by bucket.
  labelLinks: PropTypes.objectOf(PropTypes.string),
  loading: PropTypes.bool,
  oemName: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
  periods: PropTypes.arrayOf(PropTypes.object).isRequired,
  selected: PropTypes.shape({ bucket: PropTypes.string, periodId: PropTypes.string }),
  // The dealer manages its own Outstanding List (`isDealerManaged`): its admin reviews,
  // not WPC.
  selfManaged: PropTypes.bool,
  showLate: PropTypes.bool,
}

StatusMatrix.defaultProps = {
  beforePrior: null,
  labelLinks: {},
  loading: false,
  oemName: 'the OEM',
  selected: null,
  selfManaged: false,
  showLate: false,
}

export default StatusMatrix
